import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core'

import { TranslateModule } from '@ngx-translate/core'
import { BehaviorSubject, filter, takeUntil, tap, withLatestFrom } from 'rxjs'

import { DestroyDirective } from '~/core/directives'
import {
  DEFAULT_SECTOR_ORDER,
  DEFAULT_SECTOR_SORT_BY,
  PerformanceFacade,
  UiFacade,
} from '~/core/features'
import { EPerformanceSummaryFlag, IPerformanceSector } from '~/core/models'
import { AsyncBlockComponent } from '~/ui/async-block'
import { PerformanceWinTableComponent } from '~/ui/performance-win-table'

import { SECTOR_COLUMNS } from './sector-tab.constant'

@Component({
  selector: 'app-sector-tab',
  templateUrl: './sector-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [DestroyDirective],
  imports: [AsyncPipe, TranslateModule, AsyncBlockComponent, PerformanceWinTableComponent],
})
export class SectorTabComponent implements OnInit {
  private readonly destroy$ = inject(DestroyDirective).destroy$

  readonly uiFacade = inject(UiFacade)
  readonly performanceFacade = inject(PerformanceFacade)

  SECTOR_COLUMNS = SECTOR_COLUMNS
  DEFAULT_SECTOR_SORT_BY = DEFAULT_SECTOR_SORT_BY
  DEFAULT_SECTOR_ORDER = DEFAULT_SECTOR_ORDER

  readonly data$ = new BehaviorSubject<IPerformanceSector[]>([])
  readonly total$ = new BehaviorSubject<IPerformanceSector | null>(null)

  constructor() {}

  ngOnInit(): void {
    this.performanceFacade.quarter$
      .pipe(
        filter(quarter => Boolean(quarter)),
        withLatestFrom(this.performanceFacade.sectorSortBy$, this.performanceFacade.sectorOrder$),
        tap(([, sortBy, order]) =>
          this.performanceFacade.getPerformanceSector(
            sortBy || DEFAULT_SECTOR_SORT_BY,
            order || DEFAULT_SECTOR_ORDER
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe()

    this.performanceFacade.sectorData$
      .pipe(
        tap(data => {
          this.data$.next(
            (data || []).filter(item => item.summaryFlag !== EPerformanceSummaryFlag.SUMMARY_COLUMN)
          )
          const total: IPerformanceSector | null =
            data?.find(item => item.summaryFlag === EPerformanceSummaryFlag.SUMMARY_COLUMN) || null

          this.total$.next(total)
        }),
        takeUntil(this.destroy$)
      )
      .subscribe()
  }

  changeSort(sortBy: string, order: 'asc' | 'desc' | ''): void {
    this.performanceFacade.getPerformanceSector(sortBy, order)
  }
}
